if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      // 商品ギャラリー専用: カウンター分母を実際のメディア数に合わせる
      // （共通 SliderComponent の totalPages 計算は変更しない）
      this.patchViewerCounter();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    /**
     * SliderComponent は constructor 内で
     *   this.slider.addEventListener('scroll', this.update.bind(this))
     * と「元の update」を束縛するため、後から viewer.update を差し替えても
     * スクロール時の分母上書きは防げない。
     *
     * そのため商品ギャラリー viewer に限り、
     * - data-media-count（Liquid media_count）を正とし
     * - MutationObserver / scroll(rAF) / slideChanged で分母を戻す
     */
    patchViewerCounter() {
      const viewer = this.elements.viewer;
      if (!viewer || viewer.dataset.mediaCountPatched === 'true') return;

      const mediaCount = Number(viewer.dataset.mediaCount);
      if (!mediaCount) return;

      let applying = false;
      const applyMediaCount = () => {
        if (applying) return;
        const totalEl = viewer.pageTotalElement || viewer.querySelector('.slider-counter--total');
        if (!totalEl) return;
        if (totalEl.textContent === String(mediaCount)) return;

        applying = true;
        totalEl.textContent = String(mediaCount);
        applying = false;

        if (viewer.currentPageElement && viewer.currentPage > mediaCount) {
          viewer.currentPage = mediaCount;
          viewer.currentPageElement.textContent = String(mediaCount);
        }
      };

      viewer.dataset.mediaCountPatched = 'true';

      const start = () => {
        applyMediaCount();

        const totalEl = viewer.pageTotalElement || viewer.querySelector('.slider-counter--total');
        if (totalEl) {
          const observer = new MutationObserver(applyMediaCount);
          observer.observe(totalEl, { characterData: true, childList: true, subtree: true });
        }

        const slider = viewer.slider || viewer.querySelector('[id^="Slider-"]');
        if (slider) {
          // bind 済みの元 update のあとに分母を戻す
          slider.addEventListener('scroll', () => requestAnimationFrame(applyMediaCount), { passive: true });
        }

        viewer.addEventListener('slideChanged', applyMediaCount);

        // メソッド差し替えは補助（ResizeObserver 経由の initPages など）
        if (typeof viewer.update === 'function' && typeof viewer.initPages === 'function') {
          const originalUpdate = viewer.update.bind(viewer);
          viewer.update = () => {
            originalUpdate();
            applyMediaCount();
          };

          const originalInitPages = viewer.initPages.bind(viewer);
          viewer.initPages = () => {
            originalInitPages();
            applyMediaCount();
          };
        }

        applyMediaCount();
      };

      customElements.whenDefined('slider-component').then(() => {
        // SliderComponent constructor 完了待ち
        const tryStart = () => {
          if (!viewer.pageTotalElement && viewer.querySelector('.slider-counter--total') == null) {
            requestAnimationFrame(tryStart);
            return;
          }
          // update が生えるまで待つ（upgrade 完了）
          if (typeof viewer.update !== 'function') {
            requestAnimationFrame(tryStart);
            return;
          }
          start();
        };
        tryStart();
      });
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
